/**
 * RoofM2CalibrationDrawer — Sprint S3 · 2026-02c
 *
 * Admin drawer that reads `/api/admin/calibration/roof-m2-samples`
 * and renders the first-10-jobs Sheets-vs-Roof-Details variance
 * calibration report Trevor sketched: use this monthly to gauge how
 * close the floor×slope estimator is tracking to the actual sheets
 * ordered — tighten the tolerance if it's converging, investigate the
 * estimator if it's drifting.
 *
 * Opens as a right-slide panel (same shape as RulesTraceDrawer). Kept
 * intentionally small · no charts · one summary card + one table.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { X, Loader2, TrendingUp, AlertTriangle, CheckCircle2, Ruler } from 'lucide-react';

export function RoofM2CalibrationDrawer({ api, open, onClose, limit = 10 }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data: d } = await api.get(`/admin/calibration/roof-m2-samples?limit=${limit}`);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [api, limit]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      data-testid="roof-m2-calibration-drawer"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]" />

      <div
        className="relative w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="text-sm font-extrabold tracking-tight text-slate-900 inline-flex items-center gap-2">
              <Ruler size={14} className="text-orange-600" /> Roof m² Calibration
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">
                Sheets vs Roof Details · first {limit}
              </span>
            </h3>
            <p className="text-[11px] text-slate-500 mt-0.5 max-w-lg">
              Monthly office review — how close is our estimator to what we actually order? Small variance = estimator working; big variance = review the floor × slope math.
            </p>
          </div>
          <button
            data-testid="roof-m2-calibration-close-btn"
            onClick={onClose}
            className="p-1 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded"
            title="Close (Esc)"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && (
            <div className="text-sm text-slate-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading calibration samples…
            </div>
          )}
          {err && (
            <div className="bg-rose-50 border border-rose-200 text-rose-800 rounded p-3 text-sm">
              {err}
            </div>
          )}

          {data && <CalibrationBody data={data} />}
        </div>
      </div>
    </div>
  );
}

function CalibrationBody({ data }) {
  const {
    samples = [], sample_count = 0, total_logged = 0,
    mean_variance_pct, p95_variance_pct, within_tolerance_pct,
    tolerance_pct, recommendation,
  } = data;

  const recTone = recommendation?.startsWith('tighten')
    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
    : recommendation?.startsWith('keep')
    ? 'bg-sky-50 border-sky-200 text-sky-900'
    : recommendation?.startsWith('investigate')
    ? 'bg-rose-50 border-rose-200 text-rose-900'
    : 'bg-slate-50 border-slate-200 text-slate-700';

  return (
    <>
      {/* Summary card */}
      <section
        data-testid="calibration-summary"
        className="grid grid-cols-2 md:grid-cols-4 gap-3"
      >
        <StatCard label="Samples" value={sample_count} sub={`${total_logged} total logged`} />
        <StatCard label="Mean variance" value={mean_variance_pct != null ? `${mean_variance_pct}%` : '—'} sub={`tolerance ±${tolerance_pct}%`} />
        <StatCard label="p95 variance" value={p95_variance_pct != null ? `${p95_variance_pct}%` : '—'} sub="worst 5%" />
        <StatCard label="Within tolerance" value={within_tolerance_pct != null ? `${within_tolerance_pct}%` : '—'} sub="of samples" />
      </section>

      <section
        data-testid="calibration-recommendation"
        className={`border rounded-md p-3 text-sm flex items-start gap-2 ${recTone}`}
      >
        {recommendation?.startsWith('investigate')
          ? <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          : recommendation?.startsWith('tighten')
          ? <TrendingUp size={14} className="mt-0.5 flex-shrink-0" />
          : <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" />}
        <div>
          <div className="text-[10px] font-extrabold uppercase tracking-wider opacity-70">Recommendation</div>
          <div className="font-semibold leading-snug">{recommendation}</div>
        </div>
      </section>

      {/* Sample table */}
      <section>
        <h4 className="text-[10px] font-extrabold uppercase tracking-wider text-slate-500 mb-2">
          Sample rows
        </h4>
        {samples.length === 0 ? (
          <p className="text-sm text-slate-500 italic">
            No samples logged yet. The first appraisal with BOTH priced Sheet rows AND a Roof Details m² will seed the calibration log.
          </p>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-hidden">
            <table className="w-full text-xs font-mono-num" data-testid="calibration-sample-table">
              <thead className="bg-slate-50 text-slate-500 text-[10px] uppercase tracking-wider">
                <tr>
                  <th className="text-left p-2">Appraisal</th>
                  <th className="text-right p-2">Sheets m²</th>
                  <th className="text-right p-2">Roof m²</th>
                  <th className="text-right p-2">Δ m²</th>
                  <th className="text-right p-2">Variance %</th>
                  <th className="text-center p-2">OK?</th>
                  <th className="text-left p-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((s, i) => (
                  <tr
                    key={s.appraisal_id || i}
                    data-testid={`calibration-sample-row-${i}`}
                    className={i % 2 ? 'bg-white' : 'bg-slate-50/50'}
                  >
                    <td className="p-2 text-slate-700 truncate max-w-[8rem]" title={s.appraisal_id}>
                      {s.appraisal_id?.slice(-8) || '—'}
                    </td>
                    <td className="p-2 text-right">{Number(s.sheets_m2 || 0).toFixed(2)}</td>
                    <td className="p-2 text-right">{Number(s.roof_details_m2 || 0).toFixed(2)}</td>
                    <td className={`p-2 text-right font-semibold ${
                      Math.abs(s.delta_m2 || 0) < 0.01 ? 'text-slate-400'
                      : s.delta_m2 > 0 ? 'text-amber-700' : 'text-rose-700'
                    }`}>
                      {(s.delta_m2 >= 0 ? '+' : '') + Number(s.delta_m2 || 0).toFixed(2)}
                    </td>
                    <td className="p-2 text-right">
                      {s.variance_pct != null ? `${s.variance_pct}%` : '—'}
                    </td>
                    <td className="p-2 text-center">
                      {s.within_tolerance
                        ? <CheckCircle2 size={12} className="text-emerald-600 inline" strokeWidth={2.5} />
                        : <AlertTriangle size={12} className="text-amber-600 inline" strokeWidth={2.5} />}
                    </td>
                    <td className="p-2 text-slate-500 text-[10px]">{s.roof_details_source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-[10px] text-slate-400 italic">
        Trevor 2026-02c · "Fast quote with a safety margin wins the job. Use this to shrink the estimator drift, not to gate individual quotes."
      </p>
    </>
  );
}

function StatCard({ label, value, sub }) {
  return (
    <div className="border border-slate-200 rounded-md p-3">
      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="text-lg font-extrabold text-slate-900 font-mono-num leading-tight mt-0.5">{value ?? '—'}</div>
      {sub && <div className="text-[10px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}
